import {
  resetSearch,
  setCurrentBuildingID,
  setCurrentFloorID,
  setVisiblePoisFilter,
  unsetCurrentPoiID,
} from "@/features/cartography/cartographySlice";
import {
  clearDirectionsRoutes,
  setRouteComputationStatus,
} from "@/features/directions/directionsSlice";
import { setFollowUser } from "@/features/map/mapSlice";
import {
  AppMessageLevel,
  AppUI,
  addMessage,
  selectPoiCategories,
  setUIMode,
} from "../app/appSlice";
import { selectConfigItem } from "../config/configSlice";
import { selectFindMyCar } from "../find_my_car/findMyCarSlice";
import {
  selectCurrentLocation,
  selectCurrentLocationStatus,
} from "../location/locationSlice";
import {
  clearNavigation,
  resetNavigationRoute,
  setNavigationErrors,
  setNavigationFrom,
  setNavigationName,
  setNavigationStatus,
  setNavigationTo,
  setPendingNavigationRequest,
  updateNavigationRouteTags,
} from "./navigationSlice";

import { FindMyCarStatusTypes } from "@/features/find_my_car/types";
import { computeInitialWASMNavigation } from "@/features/native_routes/actions";
import type { NativeRoutesContextType } from "@/features/native_routes/NativeRoutesProvider";
import type { SitumAPIContextType } from "@/features/situm_api/SitumAPIProvider";
import { WebviewApi } from "@/features/webview_api";
import { findNearestObject } from "@/libs/geo";
import i18n from "../../app/i18n";
import store from "../../app/store";
import {
  type Building,
  CURRENT_USER_LOCATION_ID,
  CUSTOM_DESTINATION_LOCATION_ID,
  FIND_MY_CAR_LOCATION_ID,
  type Floor,
  type LatLng,
  NavigationStatus,
  type Poi,
  Position,
} from "../../libs/models";
import { raceWithAbortSignal } from "../../promises/raceWithAbortSignal";
import {
  handleSetCurrentPoiID,
  selectBuildingByPoiId,
} from "../cartography/actions";
import {
  selectCurrentBuilding,
  selectCurrentBuildingID,
  selectCurrentFloorID,
  selectStoreBuildings,
} from "../cartography/cartographySlice.selectors";
import { TTSEngine } from "../config/types";
import { RouteComputationStatus, type RouteType } from "../directions/types";
import type { WASMRoutesCalculator } from "../native_routes/routeCalculatorWASM";
import {
  emmitEventNavigationCanceled,
  emmitEventNavigationFinished,
  emmitEventNavigationRequested,
} from "../utils/eventEmmiter";
import {
  type FloorChange,
  FloorChangeType,
  LocationStatusTypes,
} from "../utils/types";
import {
  selectLastNavigationTo,
  selectNavigationFrom,
  selectNavigationRoute,
  selectNavigationRouteTags,
  selectNavigationStatus,
  selectNavigationTo,
} from "./navigationSlice.selectors";
import {
  NAVIGATION_ERROR_CODES,
  type NavigateToPoint,
  type OnNavigationStartRequestedPayload,
  type PendingNavigationRequest,
} from "./types";

export const TTS_RATE = 1;
export const TTS_RATE_MOBILE = 0.5;
export const TTS_PITCH = 1;
export const TTS_VOLUME = 1;

// These statuses are terminal for location recovery, so we fail immediately
// instead of leaving a navigation request pending.
const LOCATION_IMMEDIATE_FAILURE_STATUSES = new Set<LocationStatusTypes>([
  LocationStatusTypes.NO_PERMISSIONS,
  LocationStatusTypes.BROWSER_NO_PERMISSIONS,
]);

export const clearPendingNavigationRequest = () => {
  store.dispatch(setPendingNavigationRequest(null));
};

// API-triggered navigation always goes through the hook so the request
// lifecycle stays in one place.
export const overridePendingNavigationRequest = (
  payload: OnNavigationStartRequestedPayload,
) => {
  store.dispatch(setNavigationErrors([]));
  store.dispatch(clearDirectionsRoutes());
  store.dispatch(setUIMode(AppUI.navigation));
  store.dispatch(setNavigationStatus(NavigationStatus.STARTING));
  store.dispatch(setRouteComputationStatus(RouteComputationStatus.CALCULATING));
  store.dispatch(setPendingNavigationRequest({ payload }));
};

export const hasBlockingLocationError = () => {
  const currentLocationStatus = selectCurrentLocationStatus(store.getState());

  return (
    currentLocationStatus != null &&
    LOCATION_IMMEDIATE_FAILURE_STATUSES.has(currentLocationStatus)
  );
};

const hasValidNavigationDestination = async (
  payload: OnNavigationStartRequestedPayload,
  situmAPIContext: SitumAPIContextType,
): Promise<boolean> => {
  if (!("navigationTo" in payload)) {
    return true;
  }

  if (payload.navigationTo === FIND_MY_CAR_LOCATION_ID) {
    const findMyCar = selectFindMyCar(store.getState());

    if (findMyCar.status !== FindMyCarStatusTypes.SAVED) {
      console.error("Find My Car not found");
      return false;
    }

    return true;
  }

  const building = await selectBuildingByPoiId(
    { poiId: payload.navigationTo },
    situmAPIContext,
  );

  if (!building) {
    console.error(
      `Navigation destination POI ${payload.navigationTo} not found`,
    );
    return false;
  }

  return true;
};

const clearNavigationFilters = () => {
  // Clear search/category visibility filters so navigation can always expose
  // the resolved destination POI.
  store.dispatch(resetSearch());
  store.dispatch(setVisiblePoisFilter([]));
};

const cancelPendingNavigationStart = () => {
  clearPendingNavigationRequest();
  store.dispatch(clearNavigation());
  store.dispatch(setRouteComputationStatus(RouteComputationStatus.FINISHED));
  store.dispatch(setUIMode(AppUI.explore));
};

// Filter-based requests do not know their final destination until we have a
// current location. This resolves the target POI at the last possible moment.
const resolveNavigationPoiId = (
  payload: OnNavigationStartRequestedPayload,
): Poi["id"] | null => {
  if ("navigationTo" in payload) {
    return payload.navigationTo;
  }

  const state = store.getState();
  const currentLocation = selectCurrentLocation(state);
  const currentBuilding = selectCurrentBuilding(state);

  if (!currentLocation || !currentBuilding) {
    return null;
  }

  const poiCategoryId = payload.filter.poiCategoryIdentifier;
  const pois = currentBuilding.pois
    .filter((p) => p.categoryIds.includes(poiCategoryId))
    .toArray();

  if (pois.length === 0) {
    console.error("No navigation destination found for requested category");
    return null;
  }

  const nearestPoint = findNearestObject(
    currentLocation.latitude,
    currentLocation.longitude,
    pois.map((p) => ({
      id: p.id,
      lat: p.position.lat,
      lng: p.position.lng,
    })),
  );

  if (nearestPoint == null) {
    console.error("Unable to resolve nearest navigation destination");
    return null;
  }

  return (nearestPoint as { id: Poi["id"] }).id;
};

// Navigation start still needs one last validation to ensure the location is
// available and belongs to the same building as the destination.
export const validCurrentLocation = async (
  destinationPoiID?: number,
  building?: Building,
): Promise<boolean> => {
  const currentLocation = selectCurrentLocation(store.getState());

  // No current location OR poi not current location's building
  if (
    !currentLocation ||
    (building != null && currentLocation?.buildingId !== building.id)
  ) {
    // Display navigation error
    store.dispatch(setUIMode(AppUI.navigation));
    handleNavigationError(NAVIGATION_ERROR_CODES.no_location);

    // Select destination POI
    if (destinationPoiID != null) {
      handleSetCurrentPoiID({ poiId: destinationPoiID });
    }

    return false;
  }

  return true;
};

// This is the real runtime start once we already know origin, destination and
// the routes library to use. Deferred requests eventually end up here too.
export const startNavigation = async ({
  building,
  origin,
  destination,
  routesCalculator,
  routeType,
}: {
  building?: Building | null;
  origin?: number | null;
  destination?: number | { id?: number | null; position: Position } | null;
  routesCalculator: WASMRoutesCalculator;
  routeType?: RouteType;
}) => {
  if (!origin || !destination) return;
  if (!building) {
    console.error("no path in the building");
    return;
  }

  const destinationId =
    typeof destination === "number" ? destination : destination?.id;
  const destinationPosition =
    typeof destination !== "number" ? destination?.position : null;

  const isValidCurrentLocation = await validCurrentLocation(
    destinationId,
    building,
  );
  if (origin === CURRENT_USER_LOCATION_ID && !isValidCurrentLocation) {
    return;
  }

  if (!destinationId) return;

  const state = store.getState();
  const currentLocation = selectCurrentLocation(state);
  const navigationTags = selectNavigationRouteTags(state);
  const defaultRouteType = selectConfigItem("directions.defaultRouteType")(
    state,
  );

  // If not coming from out of route reset and initialize
  store.dispatch(clearDirectionsRoutes());
  store.dispatch(resetNavigationRoute());
  if (destinationPosition?.name) {
    store.dispatch(setNavigationName(destinationPosition.name));
  }

  store.dispatch(setFollowUser(true));
  store.dispatch(setNavigationStatus(NavigationStatus.STARTING));
  if (origin) {
    store.dispatch(setNavigationFrom(origin));
  }
  store.dispatch(setNavigationTo(destinationId));
  handleSetCurrentPoiID({ emmitEvent: false, poiId: destinationId });
  emmitEventNavigationRequested({
    destination: destinationId,
    location: {
      building_id: building.id,
      floor_id: currentLocation?.floorId ?? null,
      lat: currentLocation?.latitude ?? null,
      lng: currentLocation?.longitude ?? null,
    },
    origin,
    type: "location-based",
  });

  const type = routeType || navigationTags[0] || defaultRouteType;
  // Save type on tags to use further on
  store.dispatch(updateNavigationRouteTags([type]));
  store.dispatch(setUIMode(AppUI.navigation));
  store.dispatch(setRouteComputationStatus(RouteComputationStatus.CALCULATING));

  computeInitialWASMNavigation({
    navigationFrom: origin,
    navigationTo: destinationPosition ?? destinationId,
    routesCalculator,
    type,
  });
};

export const clearNavigationAndNotify = () => {
  store.dispatch(clearNavigation());

  if (selectNavigationStatus(store.getState()) === NavigationStatus.FINISHED) {
    return;
  }

  store.dispatch(setRouteComputationStatus(RouteComputationStatus.FINISHED));
  WebviewApi.nativeNavigationStopped();
};

export const handleNavigationError = (
  errorCode: NAVIGATION_ERROR_CODES = NAVIGATION_ERROR_CODES.untraceable_route,
) => {
  clearNavigationAndNotify();
  store.dispatch(setRouteComputationStatus(RouteComputationStatus.FINISHED));
  store.dispatch(setNavigationStatus(NavigationStatus.STOPPED));
  store.dispatch(setNavigationErrors([errorCode]));
};

// Used when the request cannot even stay pending, for example because location
// permissions are already known to be denied.
export const failPendingNavigationRequest = (
  errorCode: NAVIGATION_ERROR_CODES,
  payload?: OnNavigationStartRequestedPayload,
) => {
  clearPendingNavigationRequest();
  handleNavigationError(errorCode);

  if (payload && "navigationTo" in payload) {
    handleSetCurrentPoiID({ poiId: payload.navigationTo });
  }
};

// This bridges a deferred request into the real start flow. Cancellation is
// cooperative: each awaited step is raced against the same signal so a newer
// request can win before this one mutates navigation state.
export const completeNavigationStartRequest = (
  pendingRequest: PendingNavigationRequest,
  situmAPIContext: SitumAPIContextType,
  nativeRoutesContext: NativeRoutesContextType,
  signal?: AbortSignal,
) => {
  return raceWithAbortSignal(signal, async () => {
    const runStep = <T>(value: Promise<T> | (() => Promise<T>)) => {
      return raceWithAbortSignal(signal, value);
    };

    const poiId = resolveNavigationPoiId(pendingRequest.payload);

    if (poiId == null) {
      cancelPendingNavigationStart();
      return;
    }

    let building = await runStep(() =>
      selectBuildingByPoiId({ poiId }, situmAPIContext),
    );
    const state = store.getState();
    const currentBuildingId = selectCurrentBuildingID(state);
    const poiCategories = selectPoiCategories(state);
    const flatEarth = selectConfigItem("feature_flags.flat_earth")(state);
    let routesCalculator: WASMRoutesCalculator | undefined;

    if (!building) {
      cancelPendingNavigationStart();
      console.error("Poi not found");
      return;
    }

    if (building.id !== currentBuildingId) {
      building = await runStep(() =>
        situmAPIContext.syncLoadBuilding({ id: building.id }),
      );

      routesCalculator = await runStep(() =>
        nativeRoutesContext.initNativeLibrary({
          building,
          flatEarth,
          poiCategories,
        }),
      );
    }

    // We need this because fmc does not store information about the building :(
    if (poiId === FIND_MY_CAR_LOCATION_ID) {
      store.dispatch(setCurrentBuildingID(building.id));
    }

    clearNavigationFilters();

    clearPendingNavigationRequest();

    startNavigation({
      building,
      destination: poiId,
      origin: CURRENT_USER_LOCATION_ID,
      routesCalculator:
        routesCalculator ?? nativeRoutesContext.routesCalculator,
      routeType: pendingRequest.payload.type,
    });
  });
};

/**
 * Public entry point for navigation requests coming from the webview API.
 *
 * Direct POI requests validate the destination first. If valid, the request is
 * stored and the hook decides whether it should start immediately or wait for
 * location updates.
 */
export const onNavigationStartRequested = async (
  payload: OnNavigationStartRequestedPayload,
  situmAPIContext: SitumAPIContextType,
) => {
  const hasValidDestination = await hasValidNavigationDestination(
    payload,
    situmAPIContext,
  );

  if (!hasValidDestination) {
    return;
  }

  if (hasBlockingLocationError()) {
    failPendingNavigationRequest(NAVIGATION_ERROR_CODES.no_location, payload);
    return;
  }

  const state = store.getState();
  const navigationStatus = selectNavigationStatus(state);
  const navigationIsActive =
    navigationStatus === NavigationStatus.STARTED ||
    navigationStatus === NavigationStatus.PROGRESS;

  if (navigationIsActive) {
    const currentDestination = selectNavigationTo(state);
    const requestedDestination = resolveNavigationPoiId(payload);

    if (
      requestedDestination == null ||
      requestedDestination === currentDestination
    ) {
      return;
    }
  }

  overridePendingNavigationRequest(payload);
};

export const onNavigationFinished = async () => {
  store.dispatch(clearNavigation());
  store.dispatch(unsetCurrentPoiID());
  store.dispatch(setUIMode(AppUI.explore));
  store.dispatch(setRouteComputationStatus(RouteComputationStatus.FINISHED));
  const state = store.getState();
  const origin = selectNavigationFrom(state) ?? -1;
  const destination = selectLastNavigationTo(state) ?? -1;
  emmitEventNavigationFinished(origin, destination);
};

export const cancelNavigation = async () => {
  clearNavigationAndNotify();
  store.dispatch(unsetCurrentPoiID());
  store.dispatch(setUIMode(AppUI.explore));

  const state = store.getState();
  const origin = selectNavigationFrom(state) ?? -1;
  const destination = selectLastNavigationTo(state) ?? -1;
  emmitEventNavigationCanceled(origin, destination);
};

export const onNavigationToPointRequested = async (
  payload: NavigateToPoint,
  routesCalculator?: WASMRoutesCalculator | null,
) => {
  const isValidCurrentLocation = await validCurrentLocation(undefined);
  if (!isValidCurrentLocation) {
    return;
  }
  const state = store.getState();
  const currentLocation = selectCurrentLocation(state);
  const building = Object.values(selectStoreBuildings(state))
    .map((b) => b.value)
    .find((b) => b.id === currentLocation?.buildingId) as Building;

  if (
    !building?.floors?.find(
      (f: Floor) => f.id === Number(payload.floorIdentifier),
    ) ||
    !building.isPointInsideBounds({
      lat: payload.lat,
      lng: payload.lng,
    } as LatLng)
  ) {
    store.dispatch(
      addMessage({
        cancellable: true,
        id: "navigation_no_current_location",
        level: AppMessageLevel.ERROR,
        message: i18n.t("navigation.error_no_location_valid"),
      }),
    );
    return;
  }

  const cartesianCoordinates = building?.coordinateConverter.toCartesian({
    lat: payload.lat || 0,
    lng: payload.lng || 0,
  });

  startNavigation({
    building,
    destination: {
      id: CUSTOM_DESTINATION_LOCATION_ID,
      position: new Position(
        building.id,
        Number(payload.floorIdentifier),
        payload.lat,
        payload.lng,
        cartesianCoordinates?.x,
        cartesianCoordinates?.y,
        payload.navigationName,
      ),
    },
    origin: CURRENT_USER_LOCATION_ID,
    routesCalculator,
    routeType: payload?.type,
  });
};

export const speakIndication = ({
  text,
  onEnd = () => {},
}: {
  text: string;
  onEnd?: () => void;
}) => {
  if (!text) return;

  const ttsRate = store.getState().config.internal.tts.rate;
  const ttsPitch = store.getState().config.internal.tts.pitch;
  const ttsVolume = store.getState().config.internal.tts.volume;
  const ttsEngine = store.getState().config.internal.tts.engine;
  const isEmbed = store.getState().config.internal.embed;

  if (
    !window.speechSynthesis ||
    store.getState().config.internal.disableNavigationEvents ||
    (ttsEngine === TTSEngine.MOBILE && isEmbed)
  ) {
    console.warn(
      "Web Speech API disabled. Text-To-Speech messages will be emitted via postMessage ...",
    );
    WebviewApi.onSpeakAloudText(text, {
      lang: document.getElementsByTagName("html")[0].lang, // TODO review this if TTS uses wrong accent
      pitch: ttsPitch, // Pitch of the speech
      rate: ttsRate, // Speed of the speech
      volume: ttsVolume,
    });
    return;
  }

  const div = document.createElement("div");
  // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
  div.innerHTML = text;
  const newText = div.textContent || div.innerText || "";

  // Create a new instance of SpeechSynthesisUtterance
  const utterance = new SpeechSynthesisUtterance(newText);

  // Optionally set some parameters
  utterance.rate = ttsRate * 2; // Speed of the speech, web range [0, 1] mobile range is [0, 2]
  utterance.pitch = ttsPitch; // Pitch of the speech
  utterance.volume = ttsVolume; // Volume
  utterance.lang = document.getElementsByTagName("html")[0].lang;

  // Callbacks
  utterance.onend = onEnd;

  // Speak the text
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
};

export const handleNavigationStepClick = (indication: FloorChange) => {
  const state = store.getState();
  const currentBuilding = selectCurrentBuilding(state);
  const currentFloorID = selectCurrentFloorID(state);
  const navigationRoute = selectNavigationRoute(state);

  const floor = currentBuilding?.floors?.find(
    (f) =>
      f.level === indication.relatedLevel &&
      indication.type === FloorChangeType.ORIGIN,
  );
  if (floor && floor.id !== currentFloorID) {
    store.dispatch(setCurrentFloorID(floor.id));
  }
  if (navigationRoute) {
    store.dispatch(setFollowUser(false));
  }
};
